import { useMemo } from 'react';
import { Autocomplete, TextField, Typography, Box, ListSubheader, CircularProgress } from '@mui/material';
import { useTranslationContext } from '../../context/TranslationContext';
import { InputContainer } from '../../layout';
import { ICalendar } from '@trii/types/dist/Calendar/Calendar';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

type Props = {
  disabled: boolean;
  calendars: ICalendar[];
  userId?: string;
  value: string;
  onChange: (calendarId: string) => void;
  loading?: boolean;
};

const TaskCalendarSelect = ({ disabled, calendars, userId, value, onChange, loading = false }: Props) => {
  const t = useTranslationContext();

  const options = useMemo(() => calendars ?? [], [calendars]);

  const selected = useMemo(() => {
    const found = options.find((c) => c.id === value);
    
    // Debug: si está en modo vista y no se encuentra el calendario
    if (disabled && value && !found && !loading) {
      console.warn('TaskCalendarSelect: Calendario no encontrado', {
        calendarId: value,
        availableCalendars: options.map(c => ({ id: c.id, title: c.title })),
        totalCalendars: options.length
      });
    }
    
    return found ?? null;
  }, [options, value, disabled, loading]);

  // Modo vista: mostrar como texto estático
  if (disabled) {
    // Si está cargando los calendarios
    if (loading) {
      return (
        <InputContainer title={t('global.calendar')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
            <CircularProgress size={16} sx={{ opacity: 0.5 }} />
            <Typography variant="subtitle2" sx={{ color: (theme) => theme.palette.text.secondary, fontSize: '0.9rem' }}>
              {t('global.loading', 'Cargando...')}
            </Typography>
          </Box>
        </InputContainer>
      );
    }

    // Si no hay calendario seleccionado pero hay un value, mostrar que no se encontró
    const displayText = selected 
      ? selected.title 
      : value 
        ? t('global.calendarNotFound', 'Calendario no disponible')
        : '-';

    return (
      <InputContainer title={t('global.calendar')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          {selected && (
            <CalendarMonthIcon
              sx={{ opacity: 0.5, color: (theme) => theme.palette.text.secondary }}
              fontSize="small"
            />
          )}
          <Typography variant="subtitle2" sx={{ color: (theme) => theme.palette.text.secondary, fontSize: '0.9rem' }}>
            {displayText}
          </Typography>
        </Box>
      </InputContainer>
    );
  }

  return (
    <InputContainer title={t('global.calendar')}>
      <Autocomplete<ICalendar>
        disabled={disabled || loading}
        size="small"
        fullWidth
        options={options}
        value={selected}
        onChange={(_, cal) => onChange(cal?.id ?? '')}
        getOptionLabel={(cal) => cal?.title ?? ''}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        loading={loading}
        groupBy={
          userId
            ? (cal) =>
                cal.userId === userId
                  ? t('calendarView.myCalendars')
                  : t('calendarView.othersCalendars')
            : undefined
        }
        renderGroup={(params) => (
          <li key={params.key}>
            <ListSubheader
              component="div"
              sx={{
                lineHeight: '32px',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'text.secondary',
                //@ts-ignore
                backgroundColor: (theme) => theme.palette.background.panel,
              }}
            >
              {params.group}
            </ListSubheader>
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </li>
        )}
        slotProps={{
          popper: {
            sx: { zIndex: 1400 },
          },
          paper: {
            sx: {
              //@ts-ignore
              backgroundColor: (theme) => theme.palette.background.panel,
            },
          },
        }}
        renderInput={(params) => (
          <TextField 
            {...params} 
            placeholder={t('global.selectCalendar')}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </InputContainer>
  );
};

export default TaskCalendarSelect;
